import React, { useState, useEffect } from 'react';
import { Box, Tabs, Tab } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import CompanyIncomeStatement from './CompanyIncomeStatement';
import CompanyBalanceSheetStatement from './CompanyBalanceSheetStatement';
import CompanyCashStatement from './CompanyCashStatement';
import CompanyKeyMetricsTTM from './CompanyKeyMetricsTTM';

const CombinedFinancials = ({ data, subtab }) => {
  const navigate = useNavigate();
  const { symbol } = useParams();
  const [selectedTab, setSelectedTab] = useState(0);

  // Sync selectedTab with URL subtab parameter
  useEffect(() => {
    if (subtab) {
      const tabIndex = subtabToIndex[subtab];
      if (tabIndex !== undefined) {
        setSelectedTab(tabIndex);
      } else {
        // Invalid subtab - redirect to default (income statement)
        navigate(`/asset/${symbol}/financials`, { replace: true });
        setSelectedTab(0);
      }
    } else {
      // No subtab in URL - default to income statement
      setSelectedTab(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subtab, symbol, navigate]);

  // Map URL strings (kebab-case) to tab indices
  const subtabToIndex = {
    'income-statement': 0,
    'balance-sheet': 1,
    'cash-flow': 2,
    'key-metrics-ttm': 3
  };

  // Reverse mapping: tab index to URL string
  const indexToSubtab = [
    'income-statement',
    'balance-sheet',
    'cash-flow',
    'key-metrics-ttm'
  ];

  const handleChange = (event, newValue) => {
    setSelectedTab(newValue);

    // Update URL with new subtab
    const subtabUrl = indexToSubtab[newValue];
    navigate(`/asset/${symbol}/financials/${subtabUrl}`, { replace: true });
  };

  // Define which tabs have data - now includes growth data
  const tabs = [
    {
      label: "Income Statement",
      data: data?.incomeStatement,
      growthData: data?.incomeStatementGrowth,
      Component: CompanyIncomeStatement
    },
    {
      label: "Balance Sheet",
      data: data?.balanceSheet,
      growthData: data?.balanceSheetGrowth,
      Component: CompanyBalanceSheetStatement
    },
    {
      label: "Cash Statement",
      data: data?.cashStatement,
      growthData: data?.cashStatementGrowth,
      Component: CompanyCashStatement
    },
    {
      label: "Key Metrics TTM",
      data: data?.keyMetricsTTM,
      financialRatiosTTM: data?.financialRatiosTTM,
      Component: CompanyKeyMetricsTTM
    }
  ].filter(tab => tab.data && Object.keys(tab.data).length > 0);

  // If no tabs have data, don't render anything
  if (tabs.length === 0) return null;

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={selectedTab} onChange={handleChange}>
          {tabs.map((tab, index) => (
            <Tab key={index} label={tab.label} />
          ))}
        </Tabs>
      </Box>
      <Box>
        {tabs[selectedTab] && React.createElement(tabs[selectedTab].Component, {
          data: tabs[selectedTab].data,
          growthData: tabs[selectedTab].growthData,
          financialRatiosTTM: tabs[selectedTab].financialRatiosTTM
        })}
      </Box>
    </Box>
  );
};

export default CombinedFinancials;